import React, { useState } from 'react';
import { ArrowRight, RotateCw, Info, AlertTriangle, TrafficCone } from 'lucide-react';

// ══════════════════════════════════════════════════════════════════════════
// PILOTE QUALITÉ · O5 · Les giratoires — VERSION FRANCE
// Œuvre pédagogique interactive :
//  · Rond-point à 4 sorties vu du dessus, îlot central soigné
//  · Circulation ANTI-HORAIRE (drive on the right FR) — S → E → N → W → S
//  · 4 boutons "1ère / 2ème / 3ème / 4ème sortie" → change trajectoire ET clignotants
//  · Trajectoire animée en pointillés verts (dashoffset animate)
//  · Clignotants sur la voiture EGO qui clignotent selon la phase
//  · Flèches de sens (grises) pour indiquer la circulation anti-horaire
//  · Panneau : voie à prendre (extérieure/intérieure) + clignotants + les 4 règles d'or
// ══════════════════════════════════════════════════════════════════════════

// Rayons du giratoire (cohérent viewBox 800x800)
const CENTER = { x: 400, y: 400 };
const RING_OUTER = 200;
const RING_INNER = 105;

// ─── TRAJECTOIRES ANTI-HORAIRES ──────────────────────────────────────────
// L'usager part du SUD à (415, 620) — VOIE DE DROITE conformément au Code
// de la Route français. Il tourne à DROITE (vers l'EST) pour entrer dans
// l'anneau — sens anti-horaire vu du dessus (règle France).
//   1ère sortie → EST  (droite,  ~90° d'arc)  — voie de droite EXT = y=415
//   2ème sortie → NORD (face,    ~180° d'arc) — voie de droite au nord = x=415
//   3ème sortie → OUEST(gauche,  ~270° d'arc) — voie de droite à l'ouest = y=385
//   4ème sortie → SUD  (demi-tour, ~360° d'arc) — retour à x=385
const SORTIES = [
  {
    n: 1,
    label: '1ère sortie',
    direction: 'droite',
    voie: 'extérieure',
    clignoDroit: true,
    clignoGaucheEntree: false,
    // Voie EXT (r≈175). Entrée S (voie droite x=415) → arc ~90° → sortie E (voie droite y=415)
    trajetD: 'M 415 620 L 415 575 A 175 175 0 0 0 575 415 L 620 415',
    description: "Sortie proche à droite. Tu restes sur la voie EXTÉRIEURE tout du long.",
    consigneEntree: "Aucun clignotant à l'entrée (tu ne « tournes pas autour »).",
    consigneSortie: "Clignotant DROIT dès que tu franchis la ligne d'arrêt du giratoire.",
  },
  {
    n: 2,
    label: '2ème sortie (face)',
    direction: 'face',
    voie: 'extérieure',
    clignoDroit: true,
    clignoGaucheEntree: false,
    // Voie EXT · arc ~180° · sortie N (voie droite x=415)
    trajetD: 'M 415 620 L 415 575 A 175 175 0 0 0 415 225 L 415 180',
    description: "Sortie « en face ». Tu peux rester sur la voie EXTÉRIEURE — c'est la plus simple.",
    consigneEntree: "Aucun clignotant à l'entrée (destination « en face »).",
    consigneSortie: "Clignotant DROIT juste après avoir dépassé la 1ère sortie.",
  },
  {
    n: 3,
    label: '3ème sortie (gauche)',
    direction: 'gauche',
    voie: 'intérieure',
    clignoDroit: true,
    clignoGaucheEntree: true,
    // PÉDAGOGIE FR : entrée voie droite → glisse voie INT → RESTE INT jusqu'à
    // la 2ème sortie (nord) → contrôle angle mort DROIT + cligno DROIT + DÉCALE
    // vers voie EXT PILE au niveau de la porte 2 pour la « fermer » physiquement
    // aux entrants du nord → poursuit EXT jusqu'à la sortie ouest.
    // Arc INT (r=130) de 300° (SE) à 100° (juste avant N, "porte 2") = ~160° CCW,
    // puis courbe de décalage AU niveau de N, puis arc EXT (r=175) de 100° à 180° (W).
    trajetD: 'M 415 620 L 415 575 C 445 560 465 545 465 513 A 130 130 0 0 0 377 272 C 372 245 365 240 369 228 A 175 175 0 0 0 225 400 L 180 385',
    // Point de décalage (angle mort + cligno droit) : au niveau de la porte 2 (nord)
    decalX: 373,
    decalY: 250,
    description: "Sortie tardive à gauche. Voie INTÉRIEURE jusqu'à la 2ème sortie, puis décale voie EXTÉRIEURE PILE à la porte 2 pour la « fermer » aux entrants nord.",
    consigneEntree: "Clignotant GAUCHE à l'entrée (je tourne autour). Glisse vers la voie INTÉRIEURE.",
    consigneSortie: "Reste INT jusqu'à arriver À la 2ème sortie. LÀ : contrôle angle mort DROIT + clignotant DROIT + décale sur EXT pour FERMER LA PORTE 2 aux entrants nord. Continue EXT et sors à l'ouest.",
  },
  {
    n: 4,
    label: '4ème sortie (demi-tour)',
    direction: 'demi-tour',
    voie: 'intérieure',
    clignoDroit: true,
    clignoGaucheEntree: true,
    // PÉDAGOGIE FR : entrée voie droite → glisse voie INT → RESTE INT jusqu'à
    // la 3ème sortie (ouest) → contrôle angle mort DROIT + cligno DROIT + DÉCALE
    // vers voie EXT PILE au niveau de la porte 3 pour la « fermer » physiquement
    // aux entrants de l'ouest → poursuit EXT jusqu'à la sortie sud (retour).
    // Arc INT (r=130) de 300° (SE) à 170° (juste avant W, "porte 3") = 230° CCW.
    trajetD: 'M 415 620 L 415 575 C 445 560 465 545 465 513 A 130 130 0 1 0 272 377 C 248 400 235 420 228 430 A 175 175 0 0 0 385 575 L 385 620',
    decalX: 250,
    decalY: 405,
    description: "Demi-tour complet. Voie INTÉRIEURE jusqu'à la 3ème sortie, puis décale voie EXTÉRIEURE PILE à la porte 3 pour la « fermer » aux entrants ouest.",
    consigneEntree: "Clignotant GAUCHE FRANC à l'entrée + regard vers l'anneau. Glisse vers voie INTÉRIEURE.",
    consigneSortie: "Reste INT jusqu'à arriver À la 3ème sortie. LÀ : contrôle angle mort DROIT + clignotant DROIT + décale sur EXT pour FERMER LA PORTE 3 aux entrants ouest. Continue EXT et sors au sud (retour).",
  },
];

const REGLES_OR = [
  { icone: '🎯', titre: "Priorité à ceux qui roulent", desc: "Céder le passage aux véhicules DÉJÀ engagés dans l'anneau, quelle que soit leur voie." },
  { icone: '↪️', titre: "Sens anti-horaire (règle FR)", desc: "Tu roules à DROITE en France → tu entres par la droite et tu circules dans le sens INVERSE des aiguilles d'une montre." },
  { icone: '↩️', titre: "Voie extérieure : 1ère ou 2ème sortie", desc: "Sortie proche → pas besoin de clignotant à l'entrée. Simplement DROIT à la sortie." },
  { icone: '↺', titre: "Voie intérieure : 3ème sortie et plus", desc: "Sortie tardive → clignotant GAUCHE à l'entrée, puis glisse vers l'extérieure avant de sortir." },
  { icone: '👉', titre: "Clignotant droit avant CHAQUE sortie", desc: "Peu importe la voie ou la sortie, le clignotant droit annonce ta sortie dès que tu passes la sortie précédente." },
];

// ══════════════════════════════════════════════════════════════════════════
// Composants internes
// ══════════════════════════════════════════════════════════════════════════

function VoitureEgo({ x, y, rotation, clignoLeft, clignoRight }) {
  return (
    <g transform={`translate(${x} ${y}) rotate(${rotation})`}>
      <ellipse cx={0} cy={2} rx={22} ry={5} fill="#000" opacity={0.4} />
      <rect x={-18} y={-32} width={36} height={64} rx={14} fill="#ef4444" stroke="#0b1120" strokeWidth={2.5} />
      <ellipse cx={0} cy={-15} rx={13} ry={9} fill="#111" opacity={0.75} />
      <ellipse cx={0} cy={15} rx={12} ry={7} fill="#111" opacity={0.6} />
      <circle cx={-10} cy={-27} r={4} fill="#fef3c7" />
      <circle cx={10} cy={-27} r={4} fill="#fef3c7" />
      {clignoLeft && (
        <circle cx={-17} cy={-25} r={6} fill="#fbbf24">
          <animate attributeName="opacity" values="1;0.1;1" dur="0.6s" repeatCount="indefinite" />
        </circle>
      )}
      {clignoRight && (
        <circle cx={17} cy={-25} r={6} fill="#fbbf24">
          <animate attributeName="opacity" values="1;0.1;1" dur="0.6s" repeatCount="indefinite" />
        </circle>
      )}
      <circle cx={0} cy={-45} r={12} fill="#fff" stroke="#ef4444" strokeWidth={2.5} />
      <text x={0} y={-41} textAnchor="middle" fontSize={10} fontWeight="bold" fill="#ef4444">MOI</text>
    </g>
  );
}

function VoitureAnneau({ x, y, rotation, color }) {
  return (
    <g transform={`translate(${x} ${y}) rotate(${rotation})`}>
      <ellipse cx={0} cy={2} rx={20} ry={4} fill="#000" opacity={0.3} />
      <rect x={-16} y={-27} width={32} height={54} rx={11} fill={color} stroke="#0b1120" strokeWidth={2} />
      <ellipse cx={0} cy={-12} rx={11} ry={7} fill="#111" opacity={0.75} />
      {/* Petite flèche sur le toit indiquant le sens (haut = avant) */}
      <path d="M 0 -21 L -4 -14 L 4 -14 Z" fill="#fff" opacity={0.8} />
    </g>
  );
}

// Flèche de sens sur l'anneau (indique la circulation anti-horaire)
function FlecheSens({ cx, cy, angle }) {
  return (
    <g transform={`translate(${cx} ${cy}) rotate(${angle})`} opacity={0.55}>
      <path d="M -8 -4 L 8 -4 L 8 -8 L 16 0 L 8 8 L 8 4 L -8 4 Z" fill="#94a3b8" />
    </g>
  );
}

// ══════════════════════════════════════════════════════════════════════════
// Composant principal
// ══════════════════════════════════════════════════════════════════════════

export default function GiratoiresSvg() {
  const [sortie, setSortie] = useState(1);
  const current = SORTIES[sortie - 1];

  return (
    <div className="w-full">
      {/* Sélecteur de sortie */}
      <div className="mb-4 grid grid-cols-2 sm:grid-cols-4 gap-2">
        {SORTIES.map((s) => (
          <button
            key={s.n}
            type="button"
            onClick={() => setSortie(s.n)}
            data-testid={`o5-sortie-${s.n}`}
            className={`h-14 rounded-xl border-2 transition text-[12.5px] font-bold px-2 flex flex-col items-center justify-center gap-0.5 ${
              sortie === s.n
                ? 'border-fuchsia-500 bg-fuchsia-500/20 text-white shadow-lg shadow-fuchsia-500/30'
                : 'border-white/15 bg-white/[0.02] text-white/70 hover:border-white/30'
            }`}
          >
            <span className="text-[15px] font-serif-editorial">{s.n}<sup className="text-[10px]">e</sup> sortie</span>
            <span className="text-[9.5px] font-normal text-white/60">{s.direction}</span>
          </button>
        ))}
      </div>

      <div className="grid lg:grid-cols-[1fr,360px] gap-4">
        {/* SVG principal */}
        <div className="rounded-2xl overflow-hidden border border-white/10 bg-gradient-to-b from-[#1a2434] to-[#0a1220]">
          <svg viewBox="0 0 800 800" className="w-full h-auto block max-h-[48vh] sm:max-h-[52vh]" role="img" aria-label={`Giratoire · ${current.label}`}>
            <defs>
              <linearGradient id="o5-grass" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#4d7c3a" />
                <stop offset="100%" stopColor="#365828" />
              </linearGradient>
              <radialGradient id="o5-island" cx="50%" cy="40%">
                <stop offset="0%" stopColor="#66a144" />
                <stop offset="100%" stopColor="#3d6825" />
              </radialGradient>
              <linearGradient id="o5-road" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#3f4b5c" />
                <stop offset="100%" stopColor="#2b3546" />
              </linearGradient>
            </defs>

            {/* Herbe fond */}
            <rect width={800} height={800} fill="url(#o5-grass)" />

            {/* Anneau extérieur */}
            <circle cx={CENTER.x} cy={CENTER.y} r={RING_OUTER} fill="url(#o5-road)" />

            {/* Branches (4 routes) */}
            <rect x={370} y={0}   width={60} height={200} fill="url(#o5-road)" />
            <rect x={370} y={600} width={60} height={200} fill="url(#o5-road)" />
            <rect x={0}   y={370} width={200} height={60} fill="url(#o5-road)" />
            <rect x={600} y={370} width={200} height={60} fill="url(#o5-road)" />

            {/* Îlot central décoratif */}
            <circle cx={CENTER.x} cy={CENTER.y} r={RING_INNER + 8} fill="#e4c797" />
            <circle cx={CENTER.x} cy={CENTER.y} r={RING_INNER} fill="url(#o5-island)" />
            <g opacity={0.85}>
              <circle cx={CENTER.x - 30} cy={CENTER.y - 20} r={16} fill="#1e5027" />
              <circle cx={CENTER.x + 25} cy={CENTER.y - 10} r={14} fill="#22582f" />
              <circle cx={CENTER.x + 10} cy={CENTER.y + 30} r={18} fill="#1e5027" />
              <circle cx={CENTER.x - 20} cy={CENTER.y + 25} r={12} fill="#25612e" />
            </g>

            {/* Séparation voies dans l'anneau (pointillé blanc) */}
            <circle cx={CENTER.x} cy={CENTER.y} r={155} fill="none" stroke="#fff" strokeWidth={2.5} strokeDasharray="14 12" opacity={0.7} />

            {/* Flèches SENS DE CIRCULATION (anti-horaire vu du dessus) */}
            {/* Positionnées au milieu de l'anneau (r ≈ 175), tangentes anti-horaires */}
            {/* S (400,575) : tangente = direction EST → angle rotation 90° */}
            <FlecheSens cx={400} cy={575} angle={90} />
            {/* E (575,400) : tangente = NORD → angle 0° */}
            <FlecheSens cx={575} cy={400} angle={0} />
            {/* N (400,225) : tangente = OUEST → angle -90° (270°) */}
            <FlecheSens cx={400} cy={225} angle={-90} />
            {/* W (225,400) : tangente = SUD → angle 180° */}
            <FlecheSens cx={225} cy={400} angle={180} />

            {/* Marquage central des branches (pointillés) */}
            <line x1={400} y1={0}   x2={400} y2={200} stroke="#fff" strokeWidth={3.5} strokeDasharray="18 18" opacity={0.75} />
            <line x1={400} y1={600} x2={400} y2={800} stroke="#fff" strokeWidth={3.5} strokeDasharray="18 18" opacity={0.75} />
            <line x1={0}   y1={400} x2={200} y2={400} stroke="#fff" strokeWidth={3.5} strokeDasharray="18 18" opacity={0.75} />
            <line x1={600} y1={400} x2={800} y2={400} stroke="#fff" strokeWidth={3.5} strokeDasharray="18 18" opacity={0.75} />

            {/* 4 panneaux "Cédez le passage" aux entrées de chaque branche */}
            <g transform="translate(370 220)">
              <polygon points="0,15 15,-10 -15,-10" fill="#fff" stroke="#dc2626" strokeWidth={3} />
            </g>
            <g transform="translate(430 580)">
              <polygon points="0,-15 15,10 -15,10" fill="#fff" stroke="#dc2626" strokeWidth={3} />
            </g>
            <g transform="translate(220 430)">
              <polygon points="15,0 -10,-15 -10,15" fill="#fff" stroke="#dc2626" strokeWidth={3} />
            </g>
            <g transform="translate(580 370)">
              <polygon points="-15,0 10,15 10,-15" fill="#fff" stroke="#dc2626" strokeWidth={3} />
            </g>

            {/* Numéros des sorties — ORDRE ANTI-HORAIRE depuis l'entrée SUD */}
            {[
              { n: 1, x: 580, y: 340 },  // EST     — 1ère sortie (droite)
              { n: 2, x: 460, y: 155 },  // NORD    — 2ème sortie (face)
              { n: 3, x: 220, y: 340 },  // OUEST   — 3ème sortie (gauche)
              { n: 4, x: 460, y: 660 },  // SUD     — 4ème sortie (demi-tour, retour)
            ].map((s) => {
              const isSelected = current.n === s.n;
              return (
                <g key={s.n} transform={`translate(${s.x} ${s.y})`}>
                  <circle r={19} fill={isSelected ? '#d946ef' : '#0b1120'} stroke={isSelected ? '#fff' : '#94a3b8'} strokeWidth={2.5} />
                  <text y={5} textAnchor="middle" fontSize={16} fontWeight="900" fill={isSelected ? '#fff' : '#94a3b8'}>{s.n}</text>
                </g>
              );
            })}

            {/* Trajectoire animée verte */}
            <path
              d={current.trajetD}
              fill="none"
              stroke="#22c55e"
              strokeWidth={7}
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeDasharray="20 14"
              opacity={0.9}
              filter="drop-shadow(0 0 8px #22c55e88)"
            >
              <animate attributeName="stroke-dashoffset" from="0" to="-68" dur="1.5s" repeatCount="indefinite" />
            </path>

            {/* Marqueur ANGLE MORT + DÉCALAGE (sorties 3 et 4) : positionné À la porte à fermer */}
            {(current.n === 3 || current.n === 4) && current.decalX != null && (
              <g transform={`translate(${current.decalX} ${current.decalY})`}>
                <circle r={24} fill="#fbbf24" opacity={0.25}>
                  <animate attributeName="r" values="20;30;20" dur="1.6s" repeatCount="indefinite" />
                </circle>
                <circle r={14} fill="#0b1120" stroke="#fbbf24" strokeWidth={2.5} />
                <text y={4} textAnchor="middle" fontSize={14} fontWeight="900" fill="#fbbf24">👁</text>
                <text y={current.n === 3 ? -32 : 42} textAnchor="middle" fontSize={10.5} fontWeight="bold" fill="#fbbf24">
                  Ferme la porte {current.n === 3 ? '2' : '3'}
                </text>
                <text y={current.n === 3 ? -20 : 54} textAnchor="middle" fontSize={9} fill="#fbbf24" opacity={0.85}>
                  Angle mort D + cligno droit
                </text>
              </g>
            )}

            {/* Voitures adverses déjà dans l'anneau (sens anti-horaire) */}
            {/* Position NE (555,340) → circulation anti-horaire = heading NORD-OUEST → rotation -30° */}
            <VoitureAnneau x={555} y={340} rotation={-30} color="#3b82f6" />
            {/* Position SO (340,555) → circulation anti-horaire = heading SUD-EST → rotation 150° */}
            <VoitureAnneau x={340} y={555} rotation={150} color="#f59e0b" />

            {/* Voiture EGO (à l'entrée sud, cap vers le nord, VOIE DE DROITE règle FR) */}
            <VoitureEgo x={415} y={640} rotation={0} clignoLeft={current.clignoGaucheEntree} clignoRight={current.clignoDroit && current.n === 1} />

            {/* Bandeau bas */}
            <g transform="translate(0, 760)">
              <rect width={800} height={40} fill="#000" opacity={0.65} />
              <text x={20} y={26} fill="#fff" fontSize={14} fontWeight="bold">O5 · Les giratoires (sens anti-horaire · France)</text>
              <text x={20} y={39} fill="#94a3b8" fontSize={11}>REMC C2f — Détecter et franchir les giratoires</text>
              <circle cx={770} cy={20} r={16} fill="#dc2626" />
              <text x={770} y={25} textAnchor="middle" fill="#fff" fontSize={11} fontWeight="900">CV</text>
            </g>
          </svg>
        </div>

        {/* Panneau droit */}
        <div className="rounded-2xl border border-white/10 bg-[#0d0d0e] p-4 sm:p-5 self-start lg:max-h-[calc(100vh-300px)] lg:overflow-y-auto lg:custom-scroll" data-testid="o5-panel">
          <div className="text-fuchsia-400 text-[10.5px] font-mono-spec uppercase tracking-wider font-bold">Sortie sélectionnée</div>
          <h4 className="mt-1 font-serif-editorial text-white text-[20px] leading-tight flex items-center gap-2">
            <RotateCw size={18} className="text-fuchsia-400" style={{ transform: 'scaleX(-1)' }} />{current.label}
          </h4>
          <p className="mt-2 text-white/75 text-[13px] leading-relaxed">{current.description}</p>

          {/* Voie */}
          <div className={`mt-4 rounded-lg border-2 p-3 flex items-center gap-3 ${current.voie === 'intérieure' ? 'border-fuchsia-500 bg-fuchsia-950/25' : 'border-emerald-500 bg-emerald-950/25'}`}>
            <div className={`w-11 h-11 rounded-full flex items-center justify-center ${current.voie === 'intérieure' ? 'bg-fuchsia-500/25' : 'bg-emerald-500/25'}`}>
              <TrafficCone size={20} className={current.voie === 'intérieure' ? 'text-fuchsia-300' : 'text-emerald-300'} />
            </div>
            <div>
              <div className={`text-[10.5px] font-mono-spec uppercase tracking-wider font-bold ${current.voie === 'intérieure' ? 'text-fuchsia-300' : 'text-emerald-300'}`}>Voie à prendre</div>
              <div className="text-white font-semibold text-[14px]">Voie {current.voie.toUpperCase()}</div>
            </div>
          </div>

          {/* Clignotants */}
          <div className="mt-3 rounded-lg border border-amber-500/40 bg-amber-950/20 p-3">
            <div className="text-amber-300 text-[10.5px] font-mono-spec uppercase tracking-wider font-bold mb-1.5">🚨 Clignotants</div>
            <div className="space-y-1.5 text-white/85 text-[12.5px] leading-relaxed">
              <div className="flex items-start gap-1.5">
                <ArrowRight size={12} className="text-amber-400 mt-0.5 shrink-0 rotate-180" />
                <span><span className="font-semibold text-amber-200">À l&apos;entrée :</span> {current.consigneEntree}</span>
              </div>
              <div className="flex items-start gap-1.5">
                <ArrowRight size={12} className="text-amber-400 mt-0.5 shrink-0" />
                <span><span className="font-semibold text-amber-200">Avant la sortie :</span> {current.consigneSortie}</span>
              </div>
            </div>
          </div>

          {/* Sens de circulation FRANCE */}
          <div className="mt-3 rounded-lg border border-cyan-500/40 bg-cyan-950/20 p-3 flex items-start gap-2">
            <Info size={14} className="text-cyan-300 mt-0.5 shrink-0" />
            <div>
              <div className="text-cyan-300 text-[10.5px] font-mono-spec uppercase tracking-wider font-bold">Sens de circulation FR</div>
              <p className="mt-0.5 text-white/85 text-[12.5px] leading-relaxed">
                En France (conduite à droite), les giratoires se prennent dans le sens <span className="text-cyan-200 font-semibold">ANTI-HORAIRE</span>. À l&apos;entrée sud, tu tournes <span className="text-cyan-200 font-semibold">à droite</span> vers l&apos;est — l&apos;îlot central reste sur ta gauche tout du long.
              </p>
            </div>
          </div>

          {/* Les 5 règles d'or */}
          <div className="mt-4">
            <div className="text-white/50 text-[10.5px] font-mono-spec uppercase tracking-wider font-bold mb-2">Les règles d&apos;or</div>
            <div className="space-y-1.5">
              {REGLES_OR.map((r, i) => (
                <div key={i} className="rounded-lg border border-white/10 bg-white/[0.02] p-2.5" data-testid={`o5-regle-${i}`}>
                  <div className="flex items-start gap-2">
                    <span className="text-[18px] leading-none">{r.icone}</span>
                    <div>
                      <div className="text-white font-semibold text-[12.5px]">{r.titre}</div>
                      <p className="mt-0.5 text-white/65 text-[11.5px] leading-relaxed">{r.desc}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="mt-3 rounded-lg border border-red-500/40 bg-red-950/20 p-3 flex items-start gap-2">
            <AlertTriangle size={14} className="text-red-300 mt-0.5 shrink-0" />
            <div>
              <div className="text-red-300 text-[10.5px] font-mono-spec uppercase tracking-wider font-bold">Sanction</div>
              <p className="mt-0.5 text-white/85 text-[12px] leading-relaxed">Refus de priorité dans un giratoire : 135 € + 4 points en moins · Circulation en sens inverse : 4è classe (750 € max) + retrait de permis possible.</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
